import React, { useEffect, useState } from "react";
import {
  Button,
  Col,
  Form,
  Modal,
  Row
} from "react-bootstrap";
import { TableColumn } from "react-data-table-component";
import { FormProvider, useForm } from "react-hook-form";
import { FaRegTrashAlt } from "react-icons/fa";
import { useNavigate } from "react-router-dom";
import { ESDataTable } from "../../controls/ESDataTable";
import ESSearchDropdown from "../../controls/ESSearchDropdown";
import ESTooltip from "../../controls/ESTooltip";
import {
  addSurveyBranchLogic,
  deleteSurveyBranchLogic,
  getAllQuestionCategory,
  getAllQuestionForTrendReport,
  getAllQuestions,
  getAllSurveyBranchLogic,
  getBranchLogicByQuestionId,
  insertSurveySectionsQuestions,
} from "../../utils/ApiUrl";
import { axiosPost } from "../../utils/AxiosHelper";
import { handlePaginationAfterDelete } from "../../utils/common";
import { showToast, ToastTypeCode } from "../../utils/Constant";
import ConfirmationPopup from "../popUp/ConfirmationPopup";
import HEPopup from "../popUp/HEPopup";
import "./SurveySectionList.scss";

interface SurveyQuestionBranchLogic {
  SurveyQuestionBranchLogicId: number;
  SurveySectionQuestionId: number;
  QuestionId: number;
  QuestionResponseId: number;
  QuestionResponseOptionId: number;
  NextSurveyQuestionId: number;
  IsActive: number;
  IsStarResponse: number;
  OrganizationId: number;
  CreateUserId: number;
  UpdateUserId: number;
  CreateDate: string;
  UpdateDate: string;
  InternalKey: string;
  ConcurrencyKey: string;
  NextSurveyQuestionTitle: string;
  QuestionResponseOptionValue: string;
  ResponseTitle: string;
}

interface QuestionBranchLogicData {
  QuestionResponseId: number;
  ResponseTitle: string;
  QuestionCategoryId : number;
  Options: {
    QuestionResponseOptionId: number;
    QuestionResponseOptionValue: string;
  }[];

}

interface Option {
  label: string;
  value: number;
}

interface QuestionsType {
  QuestionId: number;
  QuestionTitle: string;
}


interface CustomTableColumn<T> extends TableColumn<T> {
  onClick?: () => void;
}

interface ChildProps {
  rowQuestionId: number;
  rowSectionQuestionId: number;
  surveySectionId: number;
  onUpdateParentValue: (newValue: boolean) => void;
  organizationId: any;
  surveyFormId: number | null | undefined;
}

const SurveySectionQuestionBranchLogic: React.FC<ChildProps> = ({
  rowQuestionId,
  rowSectionQuestionId,
  surveySectionId,
  onUpdateParentValue,
  organizationId,
  surveyFormId,
}) => {
  const [appContextdata, setAppContextdata] = useState<any>({});
  const [surveyBranchLogic, setSurveyBranchLogic] = useState<
    SurveyQuestionBranchLogic[]
  >([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(50000);
  const [totalRecords, setTotalRecords] = useState(0);
  const [sortBy, setSortBy] = useState<string>("CreateDate");
  const [sortDir, setSortDir] = useState<string>("DESC");
  const [searchText, setSearchText] = useState<string>("");
  const [showPopup, setShowPopup] = useState(false);
  const [deleteId, setDeleteId] = useState<number | null>(null);
  const [questionBranchLogicData, setQuestionBranchLogicData] = useState<
    QuestionBranchLogicData[]
  >([]);
  const [responseTitleOptions, setResponseTitleOptions] = useState<Option[]>(
    []
  );
  const [selectedResponseTitle, setSelectedResponseTitle] =
    useState<Option | null>(null);
  const [questionResponseOptions, setQuestionResponseOptions] = useState<
    Option[]
  >([]);
  const [
    selectedQuestionResponseOptionValue,
    setSelectedQuestionResponseOptionValue,
  ] = useState<Option | null>(null);
  const [questionCategories, setQuestionCategories] = useState<Option[]>([]);
  const [questionsData, setQuestionsData] = useState<Option[]>([]);
  const [formQuestionsData, setFormQuestionsData] = useState<Option[]>([]);
  const [selectedQuestionCategory, setSelectedQuestionCategory] =
    useState<Option | null>(null);
  const [selectedQuestion, setSelectedQuestion] = useState<Option | null>(null);
  const navigate = useNavigate();

  const methods = useForm({
    defaultValues: {
      responseTitle: null,
      questionResponseOption: null,
      questionCategory: null,
      question: null,
    },
  });

  const { reset } = methods;

  const fetchSurveyBranchLogic = async () => {
    setLoading(true);
    setError(null);
    try {
      const response: any = await axiosPost(getAllSurveyBranchLogic, {
        SearchText: searchText,
        IsActive: 1,
        CurrentPageNo: currentPage,
        OrganizationId: appContextdata?.Organization,
        PageSize: pageSize,
        SortDir: sortDir,
        SortBy: sortBy,
        SurveySectionQuestionId: rowSectionQuestionId,
      });
      const data = response.data.ExtraData || [];
      setSurveyBranchLogic(data);
      setTotalRecords(response.data.TotalRecords || 0);
    } catch (error) {
      setError("Error fetching data");
    } finally {
      setLoading(false);
    }
  };

  const fetchBranchLogicByQuestionId = async () : Promise<QuestionBranchLogicData[]> => {
    if (rowQuestionId) {
      setLoading(true);
      setError(null);
      let transformedData: QuestionBranchLogicData[] = [];
      try {
        const response: any = await axiosPost(getBranchLogicByQuestionId, {
          QuestionId: rowQuestionId,
        });
        const data = response.data.ExtraData || [];

        transformedData = data.reduce(
          (acc: any, item: any) => {
            const existingResponse = acc?.find(
              (res: any) => res.QuestionResponseId === item.QuestionResponseId
            );
            if (existingResponse) {
              existingResponse.Options.push({
                QuestionResponseOptionId: item.QuestionResponseOptionId,
                QuestionResponseOptionValue: item.QuestionResponseOptionValue,
              });
            } else {
              acc.push({
                QuestionResponseId: item.QuestionResponseId,
                ResponseTitle: item.QuestionResponseType,
                QuestionCategoryId: item.QuestionCategoryId,
                Options: [
                  {
                    QuestionResponseOptionId: item.QuestionResponseOptionId,
                    QuestionResponseOptionValue:
                      item.QuestionResponseOptionValue,
                  },
                ],
              });
            }
            return acc;
          },
          [] as QuestionBranchLogicData[]
        );
        setQuestionBranchLogicData(transformedData);
        const filteredTitles = transformedData
          .filter(
            (item) =>
              item.ResponseTitle !== "TextBox" &&
            item.ResponseTitle !== "3 Star Rating" &&
              item.ResponseTitle !== "5 Star Rating" &&
              item.ResponseTitle !== "10 Star Rating" &&
              item.ResponseTitle !== "Smiley" &&
              item.ResponseTitle !== "ThumbsUpDown"
            )
            ?.map((item) => ({
              label: item.ResponseTitle,
              value: item.QuestionResponseId,
            }));
            setResponseTitleOptions(filteredTitles);
            return transformedData;
      } catch (error) {
        setError("Error fetching data");
        return [];
      } finally {
        setLoading(false);
      }
    } else {
      setError("Question ID is not defined");
      return [];
    }
  };

  const fetchQuestionCategories = async (dataOfBranchLogic?: any) => {
    try {
      const response: any = await axiosPost(getAllQuestionCategory, {
        SearchText: searchText,
        IsActive: true,
        CurrentPageNo: currentPage,
        PageSize: 32000,
        SortDir: "DESC",
        SortBy: "QuestionCategoryId",
        OrganizationId: organizationId ? organizationId : -1,
      });
      const categories = response.data.ExtraData?.map((item: any) => ({
        label: item.QuestionCategoryName,
        value: item.QuestionCategoryId,
      }));
      let selectedCategoryId = dataOfBranchLogic[0].QuestionCategoryId;
      if (categories?.length > 0) {
        const selectedCategory = categories?.find(
          (category:any) => category.value === selectedCategoryId
        );
        setSelectedQuestionCategory(selectedCategory);
        FetchQuestions(dataOfBranchLogic[0].QuestionCategoryId);
        if (selectedCategory) {
          methods.reset({ questionCategory: selectedCategory });
        }
      }
      setQuestionCategories(categories);
    } catch (error) {
      setError("Error fetching question categories");
    }
  };

  const fetchAllFormQuestions = async () => {
      try {
        const response: any = await axiosPost(getAllQuestionForTrendReport, {
          FormId: surveyFormId,
          TakeTextBox: true
        });
        if (response?.data?.Status === 200) {
          const data = response?.data?.ExtraData;
          const options = data?.map((item: QuestionsType) => ({
            label: item?.QuestionTitle,
            value: item?.QuestionId,
          }));
          setFormQuestionsData(options);
          return options;
        } else {
          showToast(`${response?.data?.ErrorMessage}`, ToastTypeCode?.ERROR);
        }
      } catch (error) {
        showToast("Error Fetching Questions", ToastTypeCode?.ERROR);
      }
      return [];
    };

    const filterOutFormQuestions = (questions: Option[], formQuestions: Option[]) => {
      const formQuestionIds = new Set(formQuestions?.map((q) => q?.value));
      return questions?.filter((q) => !formQuestionIds?.has(q?.value));
    };


  const FetchQuestions = async (categoryId: number) => {
    setLoading(true);
    setError(null);
    try {
      const response: any = await axiosPost(getAllQuestions, {
        SearchText: searchText,
        IsActive: true,
        CurrentPageNo: currentPage,
        PageSize: pageSize,
        SortDir: sortDir,
        SortBy: sortBy,
        routeFlag: 1,
        QuestionCategoryId: categoryId,
        OrganizationId: organizationId ? organizationId : -1,
      });
      const data = response?.data?.ExtraData;
      let questions = data?.map((item: QuestionsType) => ({
        label: item?.QuestionTitle,
        value: item?.QuestionId,
      }));
      if (formQuestionsData?.length > 0) {
        questions = filterOutFormQuestions(questions, formQuestionsData);
        setQuestionsData(questions);
      } else {
        const formQuestions = await fetchAllFormQuestions();
        questions = filterOutFormQuestions(questions, formQuestions);
        setQuestionsData(questions);
      }
      setQuestionsData(questions);
    } catch (error) {
      setError("Error fetching data");
    } finally {
      setLoading(false);
    }
  };

  const handleResponseTitleChange = (selectedOption: any) => {
    setSelectedResponseTitle(selectedOption);
    const selectedResponse = questionBranchLogicData?.find(
      (item: any) => item.QuestionResponseId === selectedOption?.value
    );

    const options = selectedResponse?.Options?.map((option) => ({
      label: option?.QuestionResponseOptionValue,
      value: option?.QuestionResponseOptionId,
    }));
    setQuestionResponseOptions(options || []);
  };

  const handleQuestionCategoryChange = (selectedOption: Option | null) => {
    setSelectedQuestionCategory(selectedOption);
    if (selectedOption) {
      FetchQuestions(selectedOption.value);
    } else {
      setQuestionsData([]);
    }
  };

  const handleDelete = async (id: number) => {
    try {
      const response: any = await axiosPost(deleteSurveyBranchLogic, {
        SurveyQuestionBranchLogicId: id,
      });
      if (response?.data?.Status === 200) {
        setSurveyBranchLogic((prevState) =>
          prevState.filter((item) => item.SurveyQuestionBranchLogicId !== id)
        );
        handlePaginationAfterDelete(
          response?.data?.Status,
          pageSize,
          currentPage,
          setCurrentPage,
          totalRecords
        );
        showToast("Survey Branch Logic deleted successfully", ToastTypeCode?.SUCCESS);
      } else {
        showToast(`${response?.data?.ErrorMessage}`, ToastTypeCode?.ERROR);
      }
      setShowPopup(false);
      fetchSurveyBranchLogic();
    } catch (error) {
      showToast("Error deleting survey branch logic", ToastTypeCode?.ERROR);
    }
  };

  const handlePageChange = (newPage: number) => {
    setCurrentPage(newPage);
  };

  const handleSort = (column: any, sortDirection: "asc" | "desc") => {
    if (Object.keys(column)?.length === 0 && column.constructor === Object) {
      return;
    }
    setSortBy(column.sortField);
    setSortDir(sortDirection.toUpperCase());
  };

  const handleSearch = () => {
    setCurrentPage(1);
    fetchSurveyBranchLogic();
  };

  const handlePerRowsChange = async (newPerPage: any, page: any) => {
    setPageSize(newPerPage);
  };

  const confirmDelete = (id: number) => {
    setDeleteId(id);
    setShowPopup(true);
  };

  useEffect(() => {
    const fetchData = async () => {
      const dataOfBranchLogic: any = await fetchBranchLogicByQuestionId();
      await fetchQuestionCategories(dataOfBranchLogic);
    };
    if (rowQuestionId) {
      fetchData();
    }
  }, [rowQuestionId]);

  useEffect(() => {
    fetchSurveyBranchLogic();
  }, [currentPage, pageSize, sortBy, sortDir]);

  const onSubmitData = async (data: any) => {
    const payload = {
      SurveySectionQuestionId: rowSectionQuestionId ? rowSectionQuestionId : 0,
      QuestionId: rowQuestionId ? rowQuestionId : 0,
      QuestionResponseId: data.responseTitle?.value,
      QuestionResponseOptionId: data.questionResponseOption?.value,
      NextSurveyQuestionId: data.question?.value,
      IsActive: 1,
      IsStarResponse: "false",
    };
    setLoading(true);
    try {
      const response: any = await axiosPost(addSurveyBranchLogic, payload);
      if (response?.data?.Status === 200) {
        showToast("Survey Branch Logic added successfully", ToastTypeCode?.SUCCESS);
        try {
          const responseOfSurveySectionQuestionInsert: any = await axiosPost(
            insertSurveySectionsQuestions,
            {
              surveySectionId: surveySectionId,
              SurveyQuestionBranchLogicId: response.data.ExtraData,
              QuestionIds: [data.question?.value],
            }
          );
        } catch (error) {
          showToast("Something went wrong", ToastTypeCode?.ERROR);
        }
        fetchSurveyBranchLogic();
        reset({
          responseTitle: null,
          questionResponseOption: null,
          questionCategory: null,
          question: null,
        });
        onUpdateParentValue(false);
      } else {
        showToast(`${response?.data?.ErrorMessage}`, ToastTypeCode?.ERROR);
      }
    } catch (error) {
      showToast("Error adding survey branch logic", ToastTypeCode?.ERROR);
    } finally {
      setLoading(false);
    }
  };

  const columns: CustomTableColumn<SurveyQuestionBranchLogic>[] = [
    {
      name: "Actions",
      cell: (row) => (
        <>
          <button
            type="button"
            onClick={() => confirmDelete(row.SurveyQuestionBranchLogicId)}
            className="btnicon"
          > <FaRegTrashAlt  color="#ff3610" size={14} />
          </button>
        </>
      ),
    },
    {
      name: "Response Title",
      selector: (row) => row.ResponseTitle,
      sortable: true,
      sortField: "QuestionResponseId",
    },
    {
      name: "Response Option Title",
      selector: (row) => row.QuestionResponseOptionValue,
      sortable: true,
      sortField: "QuestionResponseOptionId",
    },
    {
      name: "Next Survey Question",
      cell: (row) => (
        <ESTooltip text={row?.NextSurveyQuestionTitle} placement="top">
          <span
            style={{
              display: "inline-block",
              width: "160px",
              whiteSpace: "nowrap",
              overflow: "hidden",
              textOverflow: "ellipsis",
            }}
          >
            {row?.NextSurveyQuestionTitle}
          </span>
        </ESTooltip>
      ),
      sortable: true,
      sortField: "NextSurveyQuestionId",
    },
  ];

  return (
    <HEPopup
      className="modal modal-lg"
      show={true}
      onHide={() => {
        onUpdateParentValue(false);
      }}
    >
      <Modal.Header closeButton className="border-none" style={{ zIndex: 99 }}>
        Survey Branch Logic
      </Modal.Header>
      <Modal.Body>
        <FormProvider {...methods}>
          <form onSubmit={methods.handleSubmit(onSubmitData)}>
            <div className="wrap-questions">
              <div className="questions-blk">
                <Row>
                  <Form.Group
                    as={Col}
                    md="6"
                    className="mb-3"
                    controlId="ResponseTypeId"
                  >
                    <Form.Label>
                      Response Title <span className="text-danger">*</span>
                    </Form.Label>

                    <ESSearchDropdown
                      name="responseTitle"
                      placeholder="Select Response Title"
                      options={responseTitleOptions}
                      onChange={handleResponseTitleChange}
                    />
                  </Form.Group>
                  <Form.Group
                    as={Col}
                    md="6"
                     className="mb-3"
                    controlId="ResponseTypeId"
                  >
                    <Form.Label>
                      Response Option <span className="text-danger">*</span>
                    </Form.Label>

                    <ESSearchDropdown
                      name="questionResponseOption"
                      placeholder="Select Response Option"
                      options={questionResponseOptions}
                      onChange={(selectedOption: Option | null) => {
                        setSelectedQuestionResponseOptionValue(selectedOption);
                      }}
                    />
                  </Form.Group>
                </Row>
                <Row>
                  <Form.Group as={Col} md="6"   className="mb-3">
                    <Form.Label>
                      Question Category <span className="text-danger">*</span>
                    </Form.Label>

                    <ESSearchDropdown
                      name="questionCategory"
                      placeholder="Select Question Category"
                      options={questionCategories}
                      onChange={handleQuestionCategoryChange}
                    />
                  </Form.Group>
                  <Form.Group
                    as={Col}
                    md="6"
                      className="mb-3"
                    controlId="ResponseTypeId"
                  >
                    <Form.Label>
                      Next Question <span className="text-danger">*</span>
                    </Form.Label>

                    <ESSearchDropdown
                      name="question"
                      placeholder="Select Question"
                      options={questionsData}
                      onChange={(selectedOption: Option | null) => {
                        setSelectedQuestion(selectedOption);
                      }}
                    />
                  </Form.Group>
                </Row>
                <Row>
                  <Form.Group as={Col} md="12" className="text-center">
                    <Button type="submit" className="primary-btn">
                      Submit
                    </Button>
                  </Form.Group>
                </Row>
                <br />
                {error && <p>{error}</p>}
                <ESDataTable
                  columns={columns}
                  data={surveyBranchLogic}
                  paginationTotalRows={totalRecords}
                  onChangePage={handlePageChange}
                  onSort={handleSort}
                  onChangeRowsPerPage={handlePerRowsChange}
                />
                {showPopup && (
                  <ConfirmationPopup
                    show={showPopup}
                    message="Are you sure you want to delete this survey branch logic?"
                    onConfirm={() => handleDelete(deleteId!)}
                    onCancel={() => setShowPopup(false)}
                  />
                )}
              </div>
            </div>
          </form>
        </FormProvider>
      </Modal.Body>
    </HEPopup>
  );
};

export default SurveySectionQuestionBranchLogic;
